import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Building2,
  Users,
  MapPin,
  AlertCircle,
  Loader2,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDepartmentsByPropertyTypeQuery } from '@/hooks/queries/useDropdowns';

interface ScopeSelection {
  departmentId: string;
  touchpointIds: string[];
}

interface ContractScopeSelectorProps {
  propertyTypeId: string | number | null;
  selectedScope: ScopeSelection[];
  onScopeChange: (scope: ScopeSelection[]) => void;
  className?: string;
}

export function ContractScopeSelector({
  propertyTypeId,
  selectedScope,
  onScopeChange,
  className,
}: ContractScopeSelectorProps) {
  // Fetch departments with touchpoints using single API call
  const {
    data: departments,
    isLoading,
    error,
  } = useDepartmentsByPropertyTypeQuery(propertyTypeId);

  const getPropertyIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'hotel':
        return <Building2 className="w-4 h-4" />;
      case 'restaurant':
        return <Users className="w-4 h-4" />;
      case 'compound':
        return <MapPin className="w-4 h-4" />;
      case 'store':
        return <Settings className="w-4 h-4" />;
      default:
        return <Building2 className="w-4 h-4" />;
    }
  };

  const handleDepartmentToggle = (departmentId: string, checked: boolean) => {
    if (checked) {
      // Add department with empty touchpoints array
      const newScope = [...selectedScope, { departmentId, touchpointIds: [] }];
      onScopeChange(newScope);
    } else {
      // Remove department and all its touchpoints
      const newScope = selectedScope.filter(
        item => item.departmentId !== departmentId
      );
      onScopeChange(newScope);
    }
  };

  const handleTouchpointToggle = (
    departmentId: string,
    touchpointId: string,
    checked: boolean
  ) => {
    const newScope = selectedScope.map(item => {
      if (item.departmentId === departmentId) {
        if (checked) {
          // Add touchpoint to this department
          return {
            ...item,
            touchpointIds: [...item.touchpointIds, touchpointId],
          };
        } else {
          // Remove touchpoint from this department
          return {
            ...item,
            touchpointIds: item.touchpointIds.filter(id => id !== touchpointId),
          };
        }
      }
      return item;
    });

    onScopeChange(newScope);
  };

  const isDepartmentSelected = (departmentId: string) => {
    return selectedScope.some(item => item.departmentId === departmentId);
  };

  const isTouchpointSelected = (departmentId: string, touchpointId: string) => {
    const deptScope = selectedScope.find(
      item => item.departmentId === departmentId
    );
    return deptScope ? deptScope.touchpointIds.includes(touchpointId) : false;
  };

  const getTouchpointCount = () => {
    if (!departments) return 0;
    return departments.reduce(
      (total, dept) => total + (dept.touchpoints?.length || 0),
      0
    );
  };

  const getSelectedTouchpointsCount = () => {
    return selectedScope.reduce(
      (total, item) => total + item.touchpointIds.length,
      0
    );
  };

  const getDepartmentTouchpointCount = (department: any) => {
    return department.touchpoints?.length || 0;
  };

  const getSelectedDepartmentTouchpointsCount = (department: any) => {
    const deptScope = selectedScope.find(
      item => item.departmentId === department.id.toString()
    );
    return deptScope ? deptScope.touchpointIds.length : 0;
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
            <p>Loading departments and touchpoints...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
            <h3 className="font-medium mb-2 text-red-700">
              Error Loading Data
            </h3>
            <p className="text-muted-foreground text-sm">
              {error?.message || 'Failed to load data'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!departments || departments.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-12">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-medium mb-2">No Departments Available</h3>
            <p className="text-muted-foreground text-sm">
              No departments found for this property type.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Department Access Scope
        </CardTitle>
        <CardDescription>
          Select which departments and touchpoints this user can access for
          assessments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Departments with their Touchpoints */}
        <div className="space-y-4">
          <h4 className="font-medium flex items-center gap-2">
            <Users className="w-4 h-4" />
            Departments & Touchpoints ({departments.length} departments)
          </h4>

          {departments.map(department => {
            const isSelected = isDepartmentSelected(department.id.toString());
            const touchpointCount = getDepartmentTouchpointCount(department);
            const selectedCount =
              getSelectedDepartmentTouchpointsCount(department);

            return (
              <div key={department.id} className="border rounded-lg p-4">
                {/* Department Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`dept-${department.id}`}
                      checked={isSelected}
                      onCheckedChange={checked =>
                        handleDepartmentToggle(
                          department.id.toString(),
                          !!checked
                        )
                      }
                    />
                    <Label
                      htmlFor={`dept-${department.id}`}
                      className="font-medium cursor-pointer"
                    >
                      {department.name || department.label}
                    </Label>
                  </div>
                  <Badge variant={isSelected ? 'default' : 'secondary'}>
                    {selectedCount}/{touchpointCount} touchpoints
                  </Badge>
                </div>

                {/* Department's Touchpoints */}
                {department.touchpoints &&
                  department.touchpoints.length > 0 && (
                    <div className="ml-6 space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {department.touchpoints.map(touchpoint => (
                          <div
                            key={`dept-${department.id}-tp-${touchpoint.id}`}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={`dept-${department.id}-tp-${touchpoint.id}`}
                              checked={isTouchpointSelected(
                                department.id.toString(),
                                touchpoint.id.toString()
                              )}
                              disabled={!isSelected}
                              onCheckedChange={checked =>
                                handleTouchpointToggle(
                                  department.id.toString(),
                                  touchpoint.id.toString(),
                                  !!checked
                                )
                              }
                            />
                            <Label
                              htmlFor={`dept-${department.id}-tp-${touchpoint.id}`}
                              className={cn(
                                'text-sm',
                                !isSelected
                                  ? 'text-muted-foreground cursor-not-allowed'
                                  : 'cursor-pointer'
                              )}
                            >
                              {touchpoint.name || touchpoint.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>

        {/* Overall Summary */}
        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Total Selection Summary:</span>
            <div className="flex gap-4">
              <Badge variant="outline">
                {selectedScope.length}/{departments.length} departments
              </Badge>
              <Badge variant="outline">
                {getSelectedTouchpointsCount()}/{getTouchpointCount()}{' '}
                touchpoints
              </Badge>
            </div>
          </div>
        </div>

        {/* Summary Info */}
        <div className="mt-6 p-3 bg-muted/30 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>Note:</strong> Selected departments and touchpoints will
            determine which assessment sections this user can access. Users can
            only perform assessments for their assigned scope.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
